// Der Bildschirm "Testversand" (ST-BRV-001 Schnitt B2; CM-BRV-004, CM-BRV-010).
//
// EINE Hauptaktion: "Test senden …". Sie oeffnet eine Rueckfrage, die sagt,
// welche Vorlage an welche Adresse geht und dass es sich nicht
// zurueckholen laesst; erst dort steht der primaere Knopf (Bestaetigung je
// Versand, Michael 12.10.2026). Der Fokus steht beim Oeffnen auf "Abbrechen"
// (APG Alert Dialog).
//
// Die Adresse kommt aus der Anmeldung und ist nicht eingebbar: ein Test geht
// nur an die eigene Person (CM-BRV-010).
//
// Die Statusregion ist immer im DOM; nach jeder Antwort geht der Fokus dorthin.
// Der Dialog ist nur eingehaengt, wenn er offen ist (Fluents Fokuswaechter).

import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import {
  Body1,
  Button,
  Caption1,
  Card,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  makeStyles,
  mergeClasses,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Radio,
  RadioGroup,
  Spinner,
  Title3,
  tokens,
} from '@fluentui/react-components';

import type { PlainMessage } from '../access.ts';
import { fetchBrevoTemplates, sendBrevoTest, sentSentence, type BrevoTemplateView } from '../brevo.ts';
import { newClientRequestId } from '../client-request.ts';
import { useSeitenueberschrift, useUeberschriftImInhalt } from '../shell/layout.tsx';
import { useScreenStyles } from '../shell/screen-styles.ts';
import { Vorgang } from '../shell/zustand.tsx';
import { NebenKnopf } from '../shell/nebenknopf.tsx';

const useStyles = makeStyles({
  seite: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  absatz: { margin: 0 },
  auswahl: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  detail: { display: 'block', color: tokens.colorNeutralForeground2, overflowWrap: 'anywhere' },
  name: { color: tokens.colorNeutralForeground1 },
  aktion: { alignSelf: 'flex-start' },
  leer: { minHeight: 0 },
});

export interface TestversandScreenProps {
  readonly apiBaseUrl: string;
  readonly accessToken: () => Promise<string | undefined>;
  readonly onReauthenticate: () => void;
}

type Laden =
  | { readonly art: 'laedt' }
  | { readonly art: 'geladen'; readonly templates: readonly BrevoTemplateView[]; readonly testRecipient?: string }
  | { readonly art: 'nicht-eingerichtet'; readonly message: PlainMessage }
  | { readonly art: 'fehler'; readonly message: PlainMessage };

type Ergebnis =
  | { readonly art: 'gesendet'; readonly satz: string }
  | { readonly art: 'abgelehnt'; readonly message: PlainMessage };

const TITEL_ID = 'testversand-titel';
const ADRESSE_ID = 'testversand-adresse';

function datum(iso: string | undefined): string | undefined {
  if (iso === undefined || Number.isNaN(Date.parse(iso))) return undefined;
  return new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeZone: 'Europe/Berlin' }).format(new Date(iso));
}

export function TestversandScreen({ apiBaseUrl, accessToken, onReauthenticate }: TestversandScreenProps): ReactElement {
  useUeberschriftImInhalt();
  useSeitenueberschrift('Testversand');
  const own = useStyles();
  const screen = useScreenStyles();
  const [laden, setLaden] = useState<Laden>({ art: 'laedt' });
  const [gewaehlt, setGewaehlt] = useState<number | undefined>(undefined);
  const [offen, setOffen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ergebnis, setErgebnis] = useState<Ergebnis | undefined>(undefined);
  const [hinweis, setHinweis] = useState<string | undefined>(undefined);
  const statusRef = useRef<HTMLDivElement | null>(null);
  const ausloeserRef = useRef<HTMLButtonElement | null>(null);
  const abbrechenRef = useRef<HTMLButtonElement | null>(null);
  const [fokusAufStatus, setFokusAufStatus] = useState(0);
  // Eine Kennung je bestaetigtem Versuch: entsteht beim Oeffnen der Rueckfrage.
  const anfrageRef = useRef<string>(newClientRequestId());

  const load = useCallback(async () => {
    setLaden({ art: 'laedt' });
    const token = await accessToken();
    if (token === undefined) {
      onReauthenticate();
      return;
    }
    const result = await fetchBrevoTemplates({ baseUrl: apiBaseUrl, accessToken: token });
    if (result.outcome === 'loaded') {
      setLaden({
        art: 'geladen',
        templates: result.templates,
        ...(result.testRecipient === undefined ? {} : { testRecipient: result.testRecipient }),
      });
    } else {
      setLaden({ art: result.outcome === 'unavailable' ? 'nicht-eingerichtet' : 'fehler', message: result.message });
    }
  }, [apiBaseUrl, accessToken, onReauthenticate]);

  useEffect(() => {
    void load();
  }, [load]);

  // Fokus nach dem await per Effekt auf die Rueckmeldung, nie auf <body>.
  useEffect(() => {
    if (fokusAufStatus > 0) statusRef.current?.focus();
  }, [fokusAufStatus]);

  useEffect(() => {
    if (!offen) return undefined;
    const zeiger = window.setTimeout(() => abbrechenRef.current?.focus(), 0);
    return () => window.clearTimeout(zeiger);
  }, [offen]);

  const templates = laden.art === 'geladen' ? laden.templates : [];
  const empfaenger = laden.art === 'geladen' ? laden.testRecipient : undefined;
  const auswahl = templates.find((template) => template.templateId === gewaehlt);

  const oeffnen = (): void => {
    setErgebnis(undefined);
    // Ohne Adresse ist der Knopf gesperrt (aria-disabled, fokussierbar); der Satz darunter sagt, warum.
    if (empfaenger === undefined) return;
    if (auswahl === undefined) {
      setHinweis('Wähle zuerst eine Vorlage.');
      return;
    }
    setHinweis(undefined);
    anfrageRef.current = newClientRequestId();
    setOffen(true);
  };

  const schliessen = (): void => {
    setOffen(false);
    window.setTimeout(() => ausloeserRef.current?.focus(), 0);
  };

  const senden = async (): Promise<void> => {
    if (busy || auswahl === undefined) return;
    setBusy(true);
    try {
      const token = await accessToken();
      if (token === undefined) {
        onReauthenticate();
        return;
      }
      const result = await sendBrevoTest({
        baseUrl: apiBaseUrl,
        accessToken: token,
        templateId: auswahl.templateId,
        clientRequestId: anfrageRef.current,
      });
      setErgebnis(
        result.outcome === 'sent'
          ? { art: 'gesendet', satz: sentSentence(auswahl.name, result.testRecipient) }
          : { art: 'abgelehnt', message: result.message },
      );
    } finally {
      // Der Dialog schliesst bei jeder Antwort: hinter einem modalen Dialog
      // waere die Rueckmeldung fuer Hilfsmittel verborgen.
      setBusy(false);
      setOffen(false);
      setFokusAufStatus((zaehler) => zaehler + 1);
    }
  };

  return (
    <div className={own.seite}>
      <Title3 as="h1" id={TITEL_ID}>
        Testversand
      </Title3>
      {/* Eine Karte auf voller Inhaltsbreite wie die uebrigen Pflegeseiten; die
          Einleitung ist ihr erster Absatz, in jedem Ladezustand. */}
      <Card className={mergeClasses(screen.card, own.auswahl)}>
        <Body1 as="p" className={own.absatz}>
          Schick dir eine Versandvorlage deines Verlags aus Brevo als Test an deine eigene Adresse. Andere Empfänger:innen sind nicht möglich.
        </Body1>

        {laden.art === 'laedt' ? (
          <Spinner size="small" label="Vorlagen werden geladen" />
        ) : laden.art === 'nicht-eingerichtet' || laden.art === 'fehler' ? (
          <>
            <MessageBar intent={laden.art === 'fehler' ? 'error' : 'info'}>
              <MessageBarBody>
                <MessageBarTitle>{laden.message.headline}</MessageBarTitle>
                <div>{laden.message.explanation}</div>
                <div>{laden.message.nextStep}</div>
              </MessageBarBody>
              {laden.message.retryable ? (
                <MessageBarActions>
                  <Button onClick={() => void load()}>Erneut laden</Button>
                </MessageBarActions>
              ) : null}
            </MessageBar>
            {/* Die Saetze verweisen auf "die Vorgangsnummer unten"; hier steht sie. */}
            {laden.message.correlationId === undefined ? null : <Vorgang nummer={laden.message.correlationId} eingebettet />}
          </>
        ) : templates.length === 0 ? (
          <Body1 as="p" className={own.absatz}>
            Im Brevo-Konto deines Verlags gibt es keine aktive Vorlage. Lege sie in Brevo an und lade diese Seite neu.
          </Body1>
        ) : (
          <>
            <RadioGroup
              aria-labelledby="testversand-auswahl"
              value={gewaehlt === undefined ? '' : String(gewaehlt)}
              onChange={(_, data) => {
                setGewaehlt(Number(data.value));
                setHinweis(undefined);
              }}
            >
              <Body1 as="p" id="testversand-auswahl" className={own.absatz}>
                Vorlage
              </Body1>
              {templates.map((template) => (
                <Radio
                  key={template.templateId}
                  value={String(template.templateId)}
                  label={
                    <>
                      <span className={own.name}>{template.name}</span>
                      <Caption1 className={own.detail}>
                        Betreff: {template.subject.length > 0 ? template.subject : 'ohne Betreff'}
                        {datum(template.modifiedAt) === undefined ? '' : ` · geändert am ${datum(template.modifiedAt)}`}
                      </Caption1>
                    </>
                  }
                />
              ))}
            </RadioGroup>
            <Caption1 id={ADRESSE_ID}>
              {empfaenger === undefined
                ? 'Für deine Anmeldung ist keine Bonnier-Adresse bekannt; ein Test ist deshalb nicht möglich. Wende dich an die Administration, damit deine Adresse hinterlegt wird.'
                : `Der Test geht an ${empfaenger}.`}
            </Caption1>
            {hinweis !== undefined ? <Caption1 role="alert">{hinweis}</Caption1> : null}
            <Button
              ref={(knoten: HTMLButtonElement | null) => {
                ausloeserRef.current = knoten;
              }}
              appearance="primary"
              className={own.aktion}
              disabledFocusable={empfaenger === undefined}
              aria-describedby={ADRESSE_ID}
              onClick={oeffnen}
            >
              Test senden …
            </Button>
          </>
        )}
      </Card>

      <div ref={statusRef} tabIndex={-1} role="status" aria-live="polite" aria-label="Status: Testversand" className={own.leer}>
        {ergebnis?.art === 'gesendet' ? (
          <MessageBar intent="success">
            <MessageBarBody>
              <MessageBarTitle>Test unterwegs</MessageBarTitle>
              <div>{ergebnis.satz}</div>
            </MessageBarBody>
          </MessageBar>
        ) : ergebnis?.art === 'abgelehnt' ? (
          <MessageBar intent="warning">
            <MessageBarBody>
              <MessageBarTitle>{ergebnis.message.headline}</MessageBarTitle>
              <div>{ergebnis.message.explanation}</div>
              <div>{ergebnis.message.nextStep}</div>
            </MessageBarBody>
          </MessageBar>
        ) : null}
      </div>
      {ergebnis?.art === 'abgelehnt' && ergebnis.message.correlationId !== undefined ? (
        <Vorgang nummer={ergebnis.message.correlationId} eingebettet />
      ) : null}

      {offen && auswahl !== undefined ? (
        <Dialog open modalType="alert" onOpenChange={(_, data) => (data.open ? undefined : schliessen())}>
          <DialogSurface aria-describedby="testversand-dialog-inhalt">
            <DialogBody>
              <DialogTitle>Test senden?</DialogTitle>
              <DialogContent id="testversand-dialog-inhalt">
                <Body1 as="p" className={own.absatz}>
                  Die Vorlage „{auswahl.name}“ geht als Test an {empfaenger ?? 'deine Adresse'}. Ein versendeter Test lässt sich
                  nicht zurückholen.
                </Body1>
              </DialogContent>
              <DialogActions>
                <NebenKnopf
                  ref={(knoten: HTMLButtonElement | null) => {
                    abbrechenRef.current = knoten;
                  }}
                  onClick={schliessen}
                  disabled={busy}
                >
                  Abbrechen
                </NebenKnopf>
                <Button appearance="primary" onClick={() => void senden()} disabled={busy} disabledFocusable={busy}>
                  {busy ? 'Wird gesendet …' : 'Senden'}
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      ) : null}
    </div>
  );
}
